import { doc, getDoc } from "firebase/firestore"
import type { IRaffle } from "react-ui-components/components/models/IRaffle"
import { db } from "../../firebaseFirestore"
import { hasRaffleExpired, hasReachedVisibilityStart } from "../../contexts/raffleVisibility"
import { isRaffleZipAllowed } from "../../contexts/raffleZipAccess"

export const SHARED_RAFFLE_TIMEOUT_MS = 10000

export class SharedRaffleUnavailableError extends Error {}

// Firestore reads and chunk imports cannot be cancelled. Stop waiting and ignore their
// eventual result when the deadline passes, a newer link opens, or the popup closes.
export const withSharedRaffleDeadline = <T,>(work: Promise<T>, signal: AbortSignal): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const cancel = () => finish(() => reject(new Error("Shared raffle request cancelled")))
    const timer = setTimeout(
      () => finish(() => reject(new Error("Shared raffle request timed out"))),
      SHARED_RAFFLE_TIMEOUT_MS,
    )
    const finish = (settle: () => void) => {
      clearTimeout(timer)
      signal.removeEventListener("abort", cancel)
      settle()
    }
    signal.addEventListener("abort", cancel, { once: true })
    work.then((value) => finish(() => resolve(value)), (error) => finish(() => reject(error)))
    if (signal.aborted) cancel()
  })

export const loadSharedRaffle = async (id: string): Promise<IRaffle> => {
  const raffleId = id?.trim()
  if (!raffleId || raffleId.includes("/")) throw new SharedRaffleUnavailableError()
  const snapshot = await getDoc(doc(db, "raffles", raffleId))
  if (!snapshot.exists()) throw new SharedRaffleUnavailableError()
  const raffle = { ...snapshot.data(), id: snapshot.id } as IRaffle
  if (hasRaffleExpired(raffle) || !hasReachedVisibilityStart(raffle)) {
    throw new SharedRaffleUnavailableError()
  }
  return raffle
}

export const assertSharedRaffleZip = (raffle: IRaffle, userZip: string) => {
  if (!isRaffleZipAllowed(raffle, userZip)) throw new SharedRaffleUnavailableError()
}
